import { NonEmptyList } from 'nonempty-list';
import { AppyError } from '../../../Appy';
import { Link, Resource } from '../../../Resource/Types';
import { SearchParameter, SearchParameters } from '../../../ListFilter/Types';
import { InvalidUrlError } from '@execonline-inc/url';
import { StringOption } from '../../Options/Types';
import { Maybe } from 'maybeasy';

export interface ProductLicenseOverview {
  id: number;
  organizationName: string;
  name: string;
  publicName: Maybe<string>;
  licenseQuantity: number;
  startOn: Date;
  endOn: Date;
  daysRemaining: number;
  daysFromNow: number;
  allocatedCount: number;
  activatedCount: number;
  provisionedCount: number;
  activatableCount: number;
  licenseTypeId: number;
  licenseKind: kind;
}

export const licenseFilterSelectOptions: StringOption[] = [
  {
    value: 'active',
    label: 'Active Licenses',
  },
  {
    value: 'future',
    label: 'Future Licenses',
  },
  {
    value: 'past',
    label: 'Past Licenses',
  },
  {
    value: 'all',
    label: 'All Licenses',
  },
];

export interface OverallocationAlert {
  kind: 'overallocation-alert';
  key: string;
  productLicenseOverviews: NonEmptyList<ProductLicenseOverview>;
  overallocatedBy: number;
}

export interface ExpirationAlert {
  kind: 'expiration-alert';
  key: string;
  productLicenseOverviews: NonEmptyList<ProductLicenseOverview>;
}

export interface InviteMoreParticipantsAlert {
  kind: 'invite-more-participants-alert';
  key: string;
  productLicenseOverviews: NonEmptyList<ProductLicenseOverview>;
}

export type LicenseInsight = OverallocationAlert | ExpirationAlert | InviteMoreParticipantsAlert;

export interface Upsell {
  id: number;
  title: string;
}
export type UpsellResource = Resource<Upsell>;

export interface ProductLicensesOverviewPayload {
  licensesPurchased: number;
  licensesActivatedRatio: number;
  licensesAvailable: number;
  productLicenseOverviews: ReadonlyArray<ProductLicenseOverview>;
  licenseInsights: ReadonlyArray<LicenseInsight>;
  upsells: ReadonlyArray<UpsellResource>;
}

export type ProductLicensesOverviewResource = Resource<ProductLicensesOverviewPayload>;

interface Waiting {
  kind: 'waiting';
  searchParameters: SearchParameters;
}

interface Loading {
  kind: 'loading';
  link: Link;
  searchParameters: SearchParameters;
}

export type LoadError = AppyError | InvalidUrlError;

interface LoadingError {
  kind: 'loading-error';
  link: Link;
  error: LoadError;
}

export interface Ready {
  kind: 'ready';
  link: Link;
  searchParameters: SearchParameters;
  resource: ProductLicensesOverviewResource;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
  searchParameters: [{ key: 'licenses', value: 'active' }],
});

export const loading = (link: Link, searchParameters: SearchParameters): Loading => ({
  kind: 'loading',
  searchParameters,
  link,
});

export const loadingError = (link: Link, error: LoadError): LoadingError => ({
  kind: 'loading-error',
  link,
  error,
});

export const updatedSearchParameters = (
  searchParameters: SearchParameters,
  searchParameter: SearchParameter,
) => searchParameters.filter((sp) => sp.key !== searchParameter.key).concat([searchParameter]);

export const ready = (resource: ProductLicensesOverviewResource, state: Loading): Ready => ({
  kind: 'ready',
  link: state.link,
  searchParameters: state.searchParameters,
  resource,
});

export type State = Waiting | Loading | LoadingError | Ready;

export type kind = 'past' | 'future' | 'active';
